import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiShare2,
  FiFacebook,
  FiTwitter,
  FiLink,
  FiMail,
  FiLinkedin,
  FiCopy,
  FiCheck,
  FiX
} = FiIcons;

const ShareButton = ({
  url = typeof window !== 'undefined' ? window.location.href : '',
  title = typeof document !== 'undefined' ? document.title : 'Α.Ο. Χανιά',
  description = '',
  imageUrl = '',
  className = '',
  buttonText = 'Κοινοποίηση',
  compact = false,
  iconSize = 'md',
  variant = 'primary',
  direction = 'down',
  align = 'right'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
    setCopied(false);
  };

  const handleClickOutside = (e) => {
    const target = e.target instanceof Element ? e.target : e.target?.parentElement;
    if (target?.closest?.('.share-menu-container')) return;
    setIsOpen(false);
  };

  React.useEffect(() => {
    if (isOpen) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const shareUrl = (() => {
    if (!url) return typeof window !== 'undefined' ? window.location.href : '';
    if (/^https?:\/\//i.test(url)) return url;
    if (typeof window === 'undefined') return url;
    return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
  })();

  const copyToClipboard = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
      alert('Σύνδεσμος: ' + shareUrl);
    }
  };

  const baseShareData = {
    title,
    text: description || title,
    url: shareUrl,
  };

  const handleNativeShare = async () => {
    try {
      if (!navigator.share) {
        await copyToClipboard();
        return;
      }

      let shareOptions = { ...baseShareData };

      if (imageUrl && navigator.canShare && typeof navigator.canShare === 'function') {
        try {
          const response = await fetch(imageUrl, { mode: 'cors' });
          if (response.ok) {
            const blob = await response.blob();
            const file = new File([blob], 'aoxania.jpg', { type: blob.type || 'image/jpeg' });
            if (navigator.canShare({ files: [file] })) {
              shareOptions = { ...shareOptions, files: [file] };
            }
          }
        } catch (err) {
          console.warn('Image sharing unavailable, sharing URL only:', err);
        }
      }

      await navigator.share(shareOptions);
      setIsOpen(false);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Error sharing:', error);
        await copyToClipboard();
      }
    }
  };

  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description || title}\n\n${shareUrl}`)}`;

  const buttonStyles = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white shadow-md hover:shadow-lg',
    secondary: 'bg-secondary-600 hover:bg-secondary-700 text-white shadow-md hover:shadow-lg',
    outline: 'bg-white/90 hover:bg-white border border-gray-300 text-gray-700 shadow-md hover:shadow-lg backdrop-blur-sm',
    text: 'bg-transparent hover:bg-gray-100 text-gray-700 hover:text-gray-900'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const verticalClass = direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2';
  const horizontalClass = align === 'left' ? 'left-0' : 'right-0';

  return (
    <div className="relative share-menu-container inline-block">
      <button
        type="button"
        onClick={toggleMenu}
        className={`${buttonStyles[variant]} ${compact ? 'p-2' : 'px-4 py-2'} rounded-md flex items-center transition-all duration-200 ${className}`}
        aria-label="Share"
        title={compact ? buttonText : undefined}
      >
        <SafeIcon icon={FiShare2} className={`${iconSizes[iconSize]} ${compact ? '' : 'mr-2'}`} />
        {!compact && <span>{buttonText}</span>}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: direction === 'up' ? 10 : -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: direction === 'up' ? 10 : -10 }}
            className={`absolute z-[70] w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-4 ${verticalClass} ${horizontalClass}`}
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Κοινοποίηση</h3>
              <button type="button" onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <SafeIcon icon={FiX} className="w-4 h-4" />
              </button>
            </div>

            {imageUrl && (
              <div className="mb-3 flex justify-center">
                <img src={imageUrl} alt={title} className="h-16 w-auto object-contain rounded-md" />
              </div>
            )}

            <div className="grid grid-cols-4 gap-2 mb-3">
              <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 rounded-md hover:bg-blue-50 text-blue-600 transition-colors" onClick={() => setIsOpen(false)}>
                <SafeIcon icon={FiFacebook} className="w-6 h-6 mb-1" /><span className="text-xs">Facebook</span>
              </a>
              <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 rounded-md hover:bg-blue-50 text-blue-400 transition-colors" onClick={() => setIsOpen(false)}>
                <SafeIcon icon={FiTwitter} className="w-6 h-6 mb-1" /><span className="text-xs">Twitter</span>
              </a>
              <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 rounded-md hover:bg-blue-50 text-blue-700 transition-colors" onClick={() => setIsOpen(false)}>
                <SafeIcon icon={FiLinkedin} className="w-6 h-6 mb-1" /><span className="text-xs">LinkedIn</span>
              </a>
              <a href={mailtoUrl} className="flex flex-col items-center justify-center p-3 rounded-md hover:bg-gray-50 text-gray-600 transition-colors" onClick={() => setIsOpen(false)}>
                <SafeIcon icon={FiMail} className="w-6 h-6 mb-1" /><span className="text-xs">Email</span>
              </a>
            </div>

            <div className="flex items-center space-x-2 mt-3">
              <button type="button" onClick={handleNativeShare} className="flex items-center space-x-2 w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded-md text-sm transition-colors">
                <SafeIcon icon={FiShare2} className="w-4 h-4" /><span>Κοινοποίηση</span>
              </button>
              <button type="button" onClick={copyToClipboard} className="flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 p-2 rounded-md transition-colors" title="Αντιγραφή συνδέσμου">
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4" />
              </button>
            </div>

            {copied && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-2 text-xs text-center text-green-600 font-medium">
                Ο σύνδεσμος αντιγράφηκε!
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShareButton;
